import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import jwt from 'jwt-decode';
import moment from 'moment';
import dayjs from 'dayjs';

import {
    Container, Button, Box, Card, IconButton, Stack,
    Typography, Tab, Tooltip, Link, Dialog, DialogTitle, DialogContent,
    DialogActions, Grid, TextField, ToggleButton, ToggleButtonGroup, Paper, Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Clear as CancelIcon, Done as ApproveIcon } from '@mui/icons-material';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import {
    authKey,
    APPROVE_INVOICE,
    REJECT_INVOICE,
    GET_INVOICE_LIST,
    GET_STAKEHOLDER_CATEGORY,
    DOWNLOAD_DOCUMENT,
    UPLOAD_DCUMENT,
    GET_INVOICE_DETAIL_FOR_EDIT,
    GENERATE_INVOICE,
    CALCULATE_TOTAL_INVOICE_BY_GRN_LIST
} from '../../../common/URLs';
import { PROD, allowedFileTypes, FILE_NAME_VALIDATION_ERROR } from '../../../common/constants';
import { getLocalStorageItem } from "../../../common/localStorageUtils";
import { loaderService } from "../../../common/custom-loader/loader-service";
import { toast } from 'react-toastify';
import apiClient from "../../../axios";
import { QuickSearchToolbar } from '../../../payment/common/commonFunctions';
import CustomDataGridToolbar from '../../../common/customDataGridToolbar';
import { ConfirmationPopover } from "../../../common/commonPopovers/confirmationPopover";
import { ErrorPopover } from '../../../common/ErrorPopover/errorPopover';
import { ViewIcon } from "../../../common/icons";
import { fileNameValidation } from '../../../common/commonValidations';
import { Badge } from "react-bootstrap";

// Helper to format values safely without stripping trailing zeros if sent as string
const formatToFourDecimals = (val) => {
    if (val === null || val === undefined || val === '') return '0.0000';
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toFixed(4);
};

function InvoiceList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [characterLimit] = useState(20);
    const decoded = jwt(getLocalStorageItem('id_token'));
    const URN = localStorage.getItem('urn') || decoded?.extension_URN;

    // Main screen states
    const [invoiceDetails, setInvoiceDetails] = useState([]);
    const [stakeholderDetails, setStakeholderDetails] = useState();
    const [materialTabValue, setMaterialTabValue] = useState('');
    const [tabValue, setTabValue] = useState(state?.tabValue ? state?.tabValue : "Pending");
    const [selectedInvoices, setSelectedInvoices] = useState([]);

    const [logModalOpen, setLogModalOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmOpen2, setConfirmOpen2] = useState(false);
    const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
    const [isApiCalled, setIsApiCalled] = useState(false);
    const [raiseTicket, setRaiseTicket] = useState(false);

    const [searchFilter] = useState({ "invoiceType": "", "invoiceDate": "" });

    // --- Edit Modal States ---
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [editGrnList, setEditGrnList] = useState([]);
    const [selectedGrnIds, setSelectedGrnIds] = useState([]);

    // Editable Form Fields
    const [editInvoiceNumber, setEditInvoiceNumber] = useState("");
    const [editInvoiceDate, setEditInvoiceDate] = useState(null);
    const [editComment, setEditComment] = useState("");

    // Document Upload & Calculation States
    const [documentsArr, setDocumentsArr] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileuploadError, setFileuploadError] = useState('');
    const [totalDeliveredQty, setTotalDeliveredQty] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [gstData, setGstData] = useState(null);

    useEffect(() => {
        if (stakeholderDetails?.category === 'LLE') {
            setMaterialTabValue('ChipBamboo');
        } else if (state?.materialTabValue) {
            setMaterialTabValue(state.materialTabValue);
        } else {
            setMaterialTabValue('RawBamboo');
        }
    }, [stakeholderDetails, state]);

    useEffect(() => {
        if (decoded.extension_URN) {
            getStakeDetails();
        }
    }, [decoded.extension_URN]);

    useEffect(() => {
        if (stakeholderDetails) {
            getInvoiceList();
        }
    }, [stakeholderDetails, tabValue, materialTabValue]);

    useEffect(() => {
        if (selectedFile) {
            getDocumentUploadDetails();
        }
    }, [selectedFile]);

    function getStakeDetails() {
        loaderService.showLoader();
        apiClient.post(GET_STAKEHOLDER_CATEGORY, { "urn": decoded.extension_URN, "type": "" }, {
            headers: { Authorization: 'Bearer ' + getLocalStorageItem('id_token') }
        })
            .then((response) => {
                setStakeholderDetails(response.data);
                loaderService.hideLoader();
            })
            .catch((error) => {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    }

    const getInvoiceList = (materialType) => {
        loaderService.showLoader();
        apiClient.post(GET_INVOICE_LIST, {
            "stakeholderType": stakeholderDetails?.category,
            "stakeholderUrn": stakeholderDetails?.urn,
            "invoiceType": searchFilter?.invoiceType || null,
            "invoiceDate": searchFilter?.invoiceDate || null,
            "status": tabValue,
            "materialType": materialType || materialTabValue,
        }, {
            headers: { Authorization: "Bearer " + getLocalStorageItem('id_token') }
        })
            .then((response) => {
                const list = response?.data?.invoiceList || [];
                list.forEach(element => {
                    element.invoiceDate = moment(element?.invoiceDate).format("DD/MM/YYYY");
                });
                setInvoiceDetails(list);
                setRaiseTicket(false);
                loaderService.hideLoader();
            })
            .catch((error) => {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    };

    // --- Open Edit Dialog & Fetch Invoice Details ---
    const handleOpenEditModal = (row) => {
        loaderService.showLoader();
        apiClient.post(GET_INVOICE_DETAIL_FOR_EDIT, {
            invoiceNumber: row.invoiceNo,
            id: row?.id
        }, {
            headers: { Authorization: 'Bearer ' + getLocalStorageItem('id_token') }
        })
            .then((response) => {
                if (response.data?.isSuccess) {
                    const data = response.data;
                    setEditData(data);

                    setEditInvoiceNumber(data?.invoiceDetails?.invoiceNumber || "");
                    setEditInvoiceDate(data?.invoiceDetails?.invoiceDate ? dayjs(data.invoiceDetails.invoiceDate) : null);
                    setEditComment(data?.invoiceDetails?.comment || "");

                    const docs = data?.invoiceDetails?.invoiceDocuments || [];
                    const mappedDocs = docs.map(doc => ({
                        fileName: doc.invoiceFileName,
                        blobFileName: doc.invoiceFileId,
                        containerName: doc.containerName || "pit"
                    }));
                    setDocumentsArr(mappedDocs);

                    const grns = (data.grnDetailsList || []).map(item => ({
                        ...item,
                        isDispatchedQuantitySelected: item.isDispatchedQuantitySelected ?? false
                    }));
                    setEditGrnList(grns);

                    const preSelected = grns.filter(item => item.isSelected).map(item => item.grnNo);
                    setSelectedGrnIds(preSelected);

                    const selectedRows = grns.filter(item => item.isSelected);
                    calculateTotalInvoice(selectedRows, data?.servicePoNumber);

                    setEditDialogOpen(true);
                } else {
                    toast.error(response.data?.message || "Failed to fetch invoice details for edit");
                }
                loaderService.hideLoader();
            })
            .catch((error) => {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    };

    const handleCloseEditModal = () => {
        setEditDialogOpen(false);
        setEditData(null);
        setEditGrnList([]);
        setSelectedGrnIds([]);
        setEditInvoiceNumber("");
        setEditInvoiceDate(null);
        setEditComment("");
        setDocumentsArr([]);
        setSelectedFile(null);
        setFileuploadError('');
        setTotalDeliveredQty(0);
        setTotalAmount(0);
        setGstData(null);
        setConfirmUpdateOpen(false);
    };

    const calculateTotalInvoice = (rowsToCalculate, poNumberOverride) => {
        const poNum = poNumberOverride || editData?.servicePoNumber;
        if (!rowsToCalculate || rowsToCalculate.length === 0 || !poNum) {
            setTotalDeliveredQty(0);
            setTotalAmount(0);
            setGstData(null);
            return;
        }

        const payload = {
            servicePONumber: poNum,
            totalInvoiceByGrnDetails: rowsToCalculate.map((row) => {
                const isDispatched = row?.isDispatchedQuantitySelected ?? false;
                return {
                    grnNumber: row.grnNo || row.grnNumber || "",
                    toleranceLimitValue: row.toleranceLimitValue || 0,
                    isWithInToleranceLimit: row.isWithInToleranceLimit ?? true,
                    isDispatchedQuantitySelected: isDispatched,
                    selectedQuantityType: isDispatched ? "DispatchedQuantity" : "GrnQuantity",
                    quantity: isDispatched ? (row.dispatchedQuantity || 0) : (row.grnQuantity || 0)
                };
            })
        };

        apiClient.post(CALCULATE_TOTAL_INVOICE_BY_GRN_LIST, payload, {
            headers: {
                Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
                "Content-Type": "application/json"
            }
        })
            .then((response) => {
                if (response?.data?.isSuccess) {
                    setTotalDeliveredQty(response.data.totalQuantity ?? 0);
                    setTotalAmount(response.data.totalAmount ?? 0);
                    setGstData(response.data);
                }
            })
            .catch((error) => {
                console.error("Calculation failed:", error);
            });
    };

    const handleQtyToggleChange = (grnNo, newSelection) => {
        const isDispatched = newSelection === 'Dispatched';

        setEditGrnList((prevGrnList) => {
            const updatedList = prevGrnList.map((item) => {
                if (item.grnNo === grnNo) {
                    return { ...item, isDispatchedQuantitySelected: isDispatched };
                }
                return item;
            });

            const activeSelectedRows = updatedList.filter(row => selectedGrnIds.includes(row.grnNo));
            calculateTotalInvoice(activeSelectedRows);

            return updatedList;
        });
    };

    const uploadDocument = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (allowedFileTypes.indexOf(file.type) < 0) {
            setFileuploadError("Invalid file format");
            return;
        }
        if (file.size >= 5000000) {
            setFileuploadError('The file should be less than 5MB');
            return;
        }
        if (file.name.split('.').length > 2) {
            setFileuploadError("Double extension file detected");
            return;
        }
        if (fileNameValidation(file.name)) {
            setFileuploadError(FILE_NAME_VALIDATION_ERROR);
            return;
        }

        setSelectedFile(file);
        setFileuploadError('');
    };

    function getDocumentUploadDetails() {
        loaderService.showLoader();
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("fileName", selectedFile.name);
        formData.append("containerName", "pit");

        apiClient.post(UPLOAD_DCUMENT, formData, {
            headers: { AuthKey: authKey }
        })
            .then((response) => {
                if (response.data?.blobResponse) {
                    setDocumentsArr(prev => [...prev, response.data.blobResponse]);
                }
                setSelectedFile(null);
                loaderService.hideLoader();
            })
            .catch((error) => {
                console.error(error);
                loaderService.hideLoader();
                toast.error("File upload failed");
            });
    }

    const onDeleteFile = (e, item) => {
        e.preventDefault();
        setDocumentsArr(prev => prev.filter(doc => doc !== item));
    };

    const viewdownloadDocument = (documentBlobName, finalfileName, type) => {
        loaderService.showLoader();
        return fetch(DOWNLOAD_DOCUMENT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', AuthKey: authKey },
            body: JSON.stringify({ "fileName": documentBlobName, "containerName": "pit", "file": "" })
        })
            .then((response) => response.blob())
            .then((blob) => {
                if (type === "View") {
                    const fileExtension = (finalfileName || "").split('.').pop()?.toLowerCase();
                    let fileType = 'application/octet-stream';
                    if (fileExtension === 'pdf') fileType = 'application/pdf';
                    else if (fileExtension === 'png') fileType = 'image/png';
                    else if (['jpg', 'jpeg'].includes(fileExtension)) fileType = 'image/jpeg';

                    const url = window.URL.createObjectURL(new Blob([blob], { type: fileType }));
                    window.open(url, '_blank')?.focus();
                } else {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = finalfileName;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                }
                loaderService.hideLoader();
            })
            .catch((error) => {
                console.error(error);
                loaderService.hideLoader();
            });
    };

    // Pre-validation before opening Update confirmation modal
    const handlePreUpdateCheck = () => {
        if (selectedGrnIds.length === 0) {
            toast.error("Please select at least one GRN");
            return;
        }
        if (!editInvoiceNumber) {
            toast.error("Please enter invoice number");
            return;
        }
        if (!editInvoiceDate) {
            toast.error("Please select invoice date");
            return;
        }
        if (documentsArr.length === 0) {
            toast.error("Please upload at least one invoice document");
            return;
        }

        setConfirmUpdateOpen(true);
    };

    const onConfirmUpdate = () => {
        setConfirmUpdateOpen(false);
        handleUpdateInvoice();
    };

    const handleUpdateInvoice = () => {
        loaderService.showLoader();

        const selectedRows = editGrnList.filter(row => selectedGrnIds.includes(row.grnNo));

        const docListPayload = documentsArr.map(doc => ({
            "invoiceFileId": doc?.blobFileName || doc?.invoiceFileId || "",
            "invoiceFileName": doc?.fileName || doc?.invoiceFileName || "",
            "containerName": doc?.containerName || "pit"
        }));

        const grnDetailsPayload = selectedRows.map(row => {
            const isDispatched = row?.isDispatchedQuantitySelected ?? false;
            return {
                "grnNumber": row.grnNo || row.grnNumber || "",
                "toleranceLimitValue": row.toleranceLimitValue || 0,
                "isWithInToleranceLimit": row.isWithInToleranceLimit ?? true,
                "isDispatchedQuantitySelected": isDispatched,
                "selectedQuantityType": isDispatched ? "DispatchedQuantity" : "GrnQuantity",
                "quantity": isDispatched ? (row.dispatchedQuantity || 0) : (row.grnQuantity || 0)
            };
        });

        const payload = {
            "isNeedtoUpdateInvoice": true,
            "id": editData?.invoiceDetails?.id,
            "stakeholderType": stakeholderDetails?.category || "Transporter",
            "stakeholderUrn": stakeholderDetails?.urn || URN,
            "servicePOnumber": editData?.servicePoNumber || "",
            "serviceStakeholderType": "Transporter",
            "roleName": localStorage.getItem('role') || "",
            "userName": localStorage.getItem('userName') || "",
            "isInvoiceNoUploaded": true,
            "uploadedInvoiceNumber": editInvoiceNumber,
            "comment": editComment || "",
            "isNeedtoGeneratedInvoice": false,
            "selectedInvoiceNumber": editInvoiceNumber,
            "grnNoList": selectedRows.map(item => item.grnNo || item.grnNumber || ""),
            "totalInvoiceByGrnDetails": grnDetailsPayload,
            "uploadedHarvesterInvoiceNumber": "",
            "invoiceDate": editInvoiceDate ? dayjs(editInvoiceDate).format('YYYY-MM-DD') : "",
            "isGenerateSes": false,
            "amount": gstData?.amount ?? editData?.invoiceDetails?.invoiceAmount ?? 0,
            "cgstTax": gstData?.cgstTax ?? editData?.invoiceDetails?.cgstTax ?? 0,
            "sgstTax": gstData?.sgstTax ?? editData?.invoiceDetails?.sgstTax ?? 0,
            "igstTax": gstData?.igstTax ?? editData?.invoiceDetails?.igstTax ?? 0,
            "tax": gstData?.tax ?? editData?.invoiceDetails?.invoiceTax ?? 0,
            "totalAmount": totalAmount || editData?.invoiceDetails?.totalInvoiceAmount || 0,
            "uploadedDocumentList": docListPayload
        };

        apiClient.post(GENERATE_INVOICE, payload, {
            headers: {
                Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
                "Content-Type": "application/json"
            }
        })
            .then((response) => {
                loaderService.hideLoader();
                if (response.data?.isSuccess) {
                    toast.success(response.data?.message || "Invoice updated successfully");
                    handleCloseEditModal();
                    getInvoiceList();
                } else {
                    toast.error(response.data?.message || "Failed to update invoice");
                }
            })
            .catch((error) => {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    };

    const onViewDetails = (cellValues) => {
        const categoryMap = {
            "Grower": './growerLleInvoice',
            "HIT": './hitInvoice',
            "LLE": './lleInvoice',
            "Transporter": './trInvoice'
        };
        const route = categoryMap[stakeholderDetails?.category];
        if (route) {
            navigate(route, { state: { selectedRow: cellValues.row, pageName: "invoiceList", tabValue, materialTabValue } });
        }
    };

    const onConfirmApprove = async () => {
        if (isApiCalled) return;
        setIsApiCalled(true);
        try {
            await approveInvoices();
            await new Promise((resolve) => setTimeout(resolve, 1000));
        } catch (error) {
            console.error("API failed:", error);
        } finally {
            setConfirmOpen(false);
            setIsApiCalled(false);
        }
    };

    const approveInvoices = () => {
        loaderService.showLoader();
        apiClient.post(APPROVE_INVOICE, { "invoiceNumberList": selectedInvoices }, {
            headers: { Authorization: 'Bearer ' + getLocalStorageItem('id_token') }
        })
            .then((response) => {
                if (response.data.isSuccess === true) toast.success("Invoice accepted successfully");
                else toast.error(response.data?.message || "Error");
                getInvoiceList();
                loaderService.hideLoader();
            })
            .catch((error) => {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    };

    const rejectInvoices = () => {
        loaderService.showLoader();
        apiClient.post(REJECT_INVOICE, { "invoiceNumberList": selectedInvoices }, {
            headers: { Authorization: "Bearer " + getLocalStorageItem('id_token') }
        })
            .then((response) => {
                if (response.data.isSuccess === true) toast.success(response.data?.message);
                else toast.error(response.data?.message || "Error");
                getInvoiceList();
                if (raiseTicket) navigate('./requests/new-incident');
                loaderService.hideLoader();
            })
            .catch((error) => {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    };

    const editGrnColumns = [
        { field: 'poNumber', headerName: "PO Number", flex: 1, minWidth: 140 },
        { field: 'grnNo', headerName: "GRN Number", flex: 1, minWidth: 130 },
        { field: 'consignmentNo', headerName: "Consignment No", flex: 1, minWidth: 150 },
        { field: 'vehicleRegNo', headerName: "Vehicle No", flex: 1, minWidth: 130 },
        { field: 'materialType', headerName: "Material Type", flex: 1, minWidth: 130 },
        {
            field: 'grnQuantity',
            headerName: "GRN Quantity(MT)",
            flex: 1,
            minWidth: 140,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        {
            field: 'dispatchedQuantity',
            headerName: "Dispatch Quantity(MT)",
            flex: 1,
            minWidth: 160,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        {
            field: 'rate',
            headerName: "Rate (INR)",
            flex: 1,
            minWidth: 110,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        {
            field: 'amount',
            headerName: "Amount (INR)",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        {
            field: 'tax',
            headerName: "Tax (INR)",
            flex: 1,
            minWidth: 100,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        {
            field: 'totalAmount',
            headerName: "Total Amount (INR)",
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        {
            field: 'qty',
            headerName: "Qty considered for Invoicing",
            flex: 1,
            minWidth: 220,
            renderCell: (params) => {
                const isDispatched = params?.row?.isDispatchedQuantitySelected ?? false;
                const isSelected = selectedGrnIds.includes(params.row.grnNo);

                return (
                    <ToggleButtonGroup
                        value={isDispatched ? 'Dispatched' : 'GRN'}
                        exclusive
                        disabled={!isSelected}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, newSelection) => {
                            if (newSelection !== null) {
                                handleQtyToggleChange(params.row.grnNo, newSelection);
                            }
                        }}
                    >
                        <ToggleButton value="GRN" sx={{ px: 1, py: 0.2, fontSize: '0.75rem' }}>
                            GRN
                        </ToggleButton>
                        <ToggleButton value="Dispatched" sx={{ px: 1, py: 0.2, fontSize: '0.75rem' }}>
                            Dispatch
                        </ToggleButton>
                    </ToggleButtonGroup>
                );
            }
        },
    ];

    const invoiceCol = [
        { field: 'invoiceNo', headerName: t('pit.invoiceNo'), flex: 1, minWidth: 160 },
        { field: 'invoiceType', headerName: t('pit.invoiceType'), flex: 1, minWidth: 140, hide: stakeholderDetails?.category !== "HIT" },
        { field: 'invoiceDate', headerName: t('pit.date'), flex: 1, minWidth: 120 },
        {
            field: 'quantity',
            headerName: t('pit.quantity'),
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => formatToFourDecimals(params?.value)
        },
        { field: 'invoiceStatus', headerName: t('pit.status'), flex: 1, minWidth: 130 },
        {
            field: 'invoiceDocumentList',
            headerName: 'Documents',
            flex: 1,
            minWidth: 250,
            hide: stakeholderDetails?.category !== "Transporter",
            renderCell: (params) => {
                const docs = params.value || [];
                if (!docs.length) return <Typography variant="caption" color="textSecondary">No Documents</Typography>;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1 }}>
                        {docs.map((doc, idx) => (
                            <Box key={doc.invoiceFileId || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={(e) => { e.preventDefault(); viewdownloadDocument(doc?.invoiceFileId, doc?.invoiceFileName, "View"); }}
                                    underline="hover"
                                    sx={{ cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left', wordBreak: 'break-all' }}
                                >
                                    {doc.invoiceFileName}
                                </Link>
                                <Button
                                    size="small"
                                    onClick={() => viewdownloadDocument(doc?.invoiceFileId, doc?.invoiceFileName, "Download")}
                                    sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                                >
                                    Download
                                </Button>
                            </Box>
                        ))}
                    </Box>
                );
            }
        },
        {
            field: 'actions',
            headerName: t('reg.actions'),
            flex: 1,
            minWidth: 130,
            renderCell: (cellValues) => {
                const isTransporter = stakeholderDetails?.category === "Transporter";
                const isSesCreated = cellValues.row?.isSesCreated;
                const canEdit = isTransporter && (isSesCreated === false || isSesCreated === null || isSesCreated === undefined);

                return (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={t('pit.view')}>
                            <IconButton color="primary" onClick={() => onViewDetails(cellValues)}>
                                <ViewIcon />
                            </IconButton>
                        </Tooltip>
                        {canEdit && (
                            <Tooltip title="Edit Invoice">
                                <IconButton color="secondary" onClick={() => handleOpenEditModal(cellValues.row)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                );
            }
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
            {!PROD && <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>PIT17</Typography>}

            <Stack direction={{ xs: 'column', sm: 'row' }} mb={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                <Typography variant="h5" fontWeight="600">
                    {t('pit.invoiceList')}
                </Typography>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" color="success" disabled={selectedInvoices?.length < 1} onClick={() => setConfirmOpen(true)} startIcon={<ApproveIcon />}>
                        {t('pit.accept')}
                    </Button>
                    <Button variant="contained" color="error" disabled={selectedInvoices?.length < 1} onClick={() => setConfirmOpen2(true)} startIcon={<CancelIcon />}>
                        {t('pit.reject')}
                    </Button>
                </Stack>
            </Stack>

            <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <TabContext value={materialTabValue}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <TabList onChange={(e, val) => { setMaterialTabValue(val); getInvoiceList(val); }} variant="scrollable" scrollButtons="auto">
                            {stakeholderDetails?.category !== "LLE" && <Tab label="Raw Bamboo" value="RawBamboo" />}
                            {stakeholderDetails?.category !== "Grower" && <Tab label="Chip Bamboo" value="ChipBamboo" />}
                        </TabList>
                    </Box>

                    {['RawBamboo', 'ChipBamboo'].map((material) => (
                        <TabPanel key={material} value={material} sx={{ p: 0 }}>
                            <TabContext value={tabValue}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
                                    <TabList onChange={(e, val) => setTabValue(val)} variant="scrollable" scrollButtons="auto" sx={{ px: 2 }}>
                                        <Tab label={t('pit.pending')} value="Pending" />
                                        <Tab label={t('pit.accepted')} value="Accepted" />
                                        <Tab label={t('pit.rejected')} value="Rejected" />
                                    </TabList>
                                </Box>
                                {["Pending", "Accepted", "Rejected"].map((status) => (
                                    <TabPanel key={status} value={status} sx={{ p: 2 }}>
                                        <Box sx={{ height: 'calc(100vh - 320px)', minHeight: '400px', width: '100%' }}>
                                            {invoiceDetails && (
                                                <DataGrid
                                                    rows={invoiceDetails}
                                                    columns={invoiceCol}
                                                    pageSize={10}
                                                    getRowId={(row) => row.invoiceNo}
                                                    components={{ Toolbar: QuickSearchToolbar }}
                                                    checkboxSelection={status === "Pending"}
                                                    onSelectionModelChange={status === "Pending" ? (newSelection => setSelectedInvoices(newSelection)) : undefined}
                                                    disableSelectionOnClick
                                                    sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5' } }}
                                                />
                                            )}
                                        </Box>
                                    </TabPanel>
                                ))}
                            </TabContext>
                        </TabPanel>
                    ))}
                </TabContext>
            </Card>

            {/* --- Edit Invoice Dialog --- */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditModal} maxWidth="xl" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', pb: 2 }}>
                    Edit Invoice - {editData?.invoiceDetails?.invoiceNumber}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        {/* 1. Header Fields Row */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="PO Number"
                                value={editData?.servicePoNumber || ''}
                                fullWidth
                                size="small"
                                disabled
                                variant="filled"
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Invoice Number "
                                value={editInvoiceNumber}
                                onChange={(e) => setEditInvoiceNumber(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                inputProps={{ maxLength: 20 }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1vh' }}>
                                <Badge bg={`${editInvoiceNumber?.length > characterLimit ? 'danger' : 'primary'}`}>{editInvoiceNumber?.length}/{characterLimit}</Badge>
                            </div>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label="Invoice Date "
                                    inputFormat="DD/MM/YYYY"
                                    value={editInvoiceDate}
                                    onChange={(newValue) => setEditInvoiceDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth size="small" required />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* 2. Calculated Totals Banner */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f4f8', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Total Delivered Qty (MT)"
                                            value={formatToFourDecimals(totalDeliveredQty || editData?.invoiceDetails?.weight)}
                                            fullWidth
                                            size="small"
                                            disabled
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Total Amount (INR)"
                                            value={formatToFourDecimals(totalAmount || editData?.invoiceDetails?.totalInvoiceAmount)}
                                            fullWidth
                                            size="small"
                                            disabled
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* 3. Supporting Documents & Comments */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom fontWeight="600">
                                Upload Invoice Documents *
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', minHeight: '135px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadFileIcon />}
                                        size="small"
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Upload File
                                        <input type="file" hidden accept="image/jpeg, image/jpg, image/png, .pdf" onChange={uploadDocument} />
                                    </Button>
                                    <Typography variant="caption" color="textSecondary">
                                        {t('preReg.uploadFileTypeNote')}
                                    </Typography>
                                </Box>
                                {fileuploadError && <Typography variant="caption" color="error" display="block">{fileuploadError}</Typography>}

                                <Stack spacing={1} sx={{ mt: 1 }}>
                                    {documentsArr?.map((item, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', p: 1, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                                                {item?.fileName || item?.invoiceFileName}
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                <Button size="small" onClick={() => viewdownloadDocument(item?.blobFileName || item?.invoiceFileId, item?.fileName || item?.invoiceFileName, "View")}>
                                                    View
                                                </Button>
                                                <IconButton size="small" color="error" onClick={(e) => onDeleteFile(e, item)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom fontWeight="600">
                                Comment
                            </Typography>
                            <TextField
                                value={editComment}
                                onChange={(e) => setEditComment(e.target.value)}
                                fullWidth
                                multiline
                                rows={4.8}
                                size="small"
                                placeholder="Add comments here..."
                            />
                        </Grid>
                    </Grid>

                    {/* 4. Consignment Details Table */}
                    <Typography variant="subtitle1" fontWeight="600" sx={{ mt: 3, mb: 1, color: 'text.primary' }}>
                        Consignment Details (Select GRNs & Quantity Option)
                    </Typography>

                    <Box sx={{ height: 350, width: '100%' }}>
                        <DataGrid
                            rows={editGrnList}
                            columns={editGrnColumns}
                            pageSize={5}
                            getRowId={(row) => row.grnNo}
                            checkboxSelection
                            selectionModel={selectedGrnIds}
                            onSelectionModelChange={(newSelection) => {
                                setSelectedGrnIds(newSelection);
                                const selectedRows = editGrnList.filter(row => newSelection.includes(row.grnNo));
                                calculateTotalInvoice(selectedRows);
                            }}
                            components={{ Toolbar: CustomDataGridToolbar }}
                            disableSelectionOnClick
                            sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5' } }}
                        />
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <Button onClick={handleCloseEditModal} color="inherit" sx={{ fontWeight: 'bold' }}>Cancel</Button>
                    <Button onClick={handlePreUpdateCheck} variant="contained" color="primary" disableElevation sx={{ fontWeight: 'bold' }}>
                        Update Invoice
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Popovers */}
            <ConfirmationPopover onOpen={confirmOpen} onConfirm={onConfirmApprove} onClose={() => setConfirmOpen(false)} action="Accept Invoice" />
            <ConfirmationPopover onOpen={confirmOpen2} onConfirm={() => { setConfirmOpen2(false); rejectInvoices(); }} onClose={() => setConfirmOpen2(false)} action="Reject Invoice" raiseTicketFlag={raiseTicket} handleRaiseTicket={() => setRaiseTicket(!raiseTicket)} />
            <ConfirmationPopover onOpen={confirmUpdateOpen} onConfirm={onConfirmUpdate} onClose={() => setConfirmUpdateOpen(false)} action="Update Invoice" />

            <Dialog maxWidth='xs' fullWidth open={logModalOpen}>
                <DialogContent>
                    <ErrorPopover title={"ERROR"} navigate={navigate} goToStakeHolderDashboard="goToStakeHolderDashboard" hideLink={true} />
                </DialogContent>
            </Dialog>
        </Container>
    );
}

export default InvoiceList;
